/**
 * sirus/AchievementsPanel — R232q21 Phase 4.
 *
 * Modal Sirus 3.3.5a achievements browser. Toggled via the `Y` micro-menu
 * button (panel flag `achievements`). Two-column layout — collapsible
 * category list on the left, 4-column achievement grid on the right.
 *
 * Cards show 64×64 placeholder icon (coloured circle with category number),
 * Cinzel name, EB Garamond italic description, gold pill with point value,
 * and a bronze/silver/gold rarity border keyed off the points threshold.
 *
 * Mock data — 30 placeholder achievements bucketed across 7 categories.
 * Real achievement scaffolding lands later when `state/achievements-store`
 * exists. Pure UI for now.
 */

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';

import { SirusFrame } from './SirusFrame.js';
import { useUiPanels } from './MicroMenu.js';

/* ─────────────────────────────────────────────────────────────────
 * Categories + mock data
 * ──────────────────────────────────────────────────────────────── */

type CategoryId = 'all' | 'dungeons' | 'pvp' | 'professions' | 'lore' | 'social' | 'rewards';

interface CategoryDef {
  id: CategoryId;
  label: string;
  colour: string;
}

const CATEGORIES: CategoryDef[] = [
  { id: 'all',         label: 'Все',          colour: '#C9A96A' },
  { id: 'dungeons',    label: 'Подземелья',   colour: '#C45656' },
  { id: 'pvp',         label: 'PvP',          colour: '#9C4A4A' },
  { id: 'professions', label: 'Профессии',    colour: '#5C8A4F' },
  { id: 'lore',        label: 'Лор',          colour: '#6F7DA8' },
  { id: 'social',      label: 'Социальные',   colour: '#A87CB0' },
  { id: 'rewards',     label: 'Награды',      colour: '#D9A24F' },
];

interface Achievement {
  id: string;
  name: string;
  desc: string;
  points: number;
  category: Exclude<CategoryId, 'all'>;
  earnedAt?: string;
}

const ACHIEVEMENTS: Achievement[] = [
  { id: 'a01', name: 'Первый шаг',        desc: 'Достигните 10-го уровня в любой стезе.',           points: 10, category: 'lore', earnedAt: '03 травня' },
  { id: 'a02', name: 'Покоритель тьмы',   desc: 'Завершите 5 подземелий нормального уровня.',        points: 25, category: 'dungeons' },
  { id: 'a03', name: 'Мастер ремесла',    desc: 'Поднимите любую профессию до 225.',                 points: 20, category: 'professions' },
  { id: 'a04', name: 'Кровь врагов',      desc: 'Победите 100 игроков на полях боя.',                points: 30, category: 'pvp' },
  { id: 'a05', name: 'Разведчик мира',    desc: 'Откройте все районы Перешейка.',                    points: 15, category: 'lore', earnedAt: '01 травня' },
  { id: 'a06', name: 'Тёмный страж',      desc: 'Завершите Цитадель Ледяной Короны на героике.',     points: 50, category: 'dungeons' },
  { id: 'a07', name: 'Звон молотов',      desc: 'Скуйте 50 предметов в кузнечном деле.',             points: 15, category: 'professions' },
  { id: 'a08', name: 'Друг таверны',      desc: 'Поешьте в каждой столице фракции.',                 points: 10, category: 'social', earnedAt: '02 травня' },
  { id: 'a09', name: 'Гладиатор арены',   desc: 'Получите рейтинг 2200 в арене 3v3.',                points: 75, category: 'pvp' },
  { id: 'a10', name: 'Легенда Стезеведа', desc: 'Прокачайте все три ветви талантов.',                points: 35, category: 'rewards' },
  { id: 'a11', name: 'Зов крови',         desc: 'Победите дракона Смертокрыла.',                     points: 50, category: 'lore' },
  { id: 'a12', name: 'Алхимик-мечтатель', desc: 'Сварите 25 редких эликсиров.',                      points: 20, category: 'professions' },
  { id: 'a13', name: 'Призрачный охотник',desc: 'Найдите 10 редких NPC в открытом мире.',            points: 25, category: 'lore' },
  { id: 'a14', name: 'Знаток гильдий',    desc: 'Состойте в гильдии 30 дней.',                       points: 15, category: 'social' },
  { id: 'a15', name: 'Покоритель Ульдуара', desc: 'Завершите Ульдуар 25-человечный.',                points: 60, category: 'dungeons' },
  { id: 'a16', name: 'Стражник чести',    desc: 'Заработайте 50 000 очков чести.',                   points: 30, category: 'pvp' },
  { id: 'a17', name: 'Лучник из легенд',  desc: 'Изготовьте лук эпического качества.',               points: 25, category: 'professions' },
  { id: 'a18', name: 'Ход истории',       desc: 'Завершите цепочку «Дозор Аиваре».',                 points: 20, category: 'lore' },
  { id: 'a19', name: 'Глас гильдии',      desc: 'Отправьте 1000 сообщений в гильдчат.',              points: 15, category: 'social' },
  { id: 'a20', name: 'Хозяин подземелий', desc: 'Завершите все классические подземелья.',            points: 40, category: 'dungeons' },
  { id: 'a21', name: 'Ярость Альтерака',  desc: 'Победите в 25 битвах за Ущелье Альтерака.',         points: 25, category: 'pvp' },
  { id: 'a22', name: 'Аромат трав',       desc: 'Соберите 1000 образцов трав.',                      points: 15, category: 'professions' },
  { id: 'a23', name: 'Хранитель тайн',    desc: 'Прочтите 30 книг лора в библиотеках.',              points: 20, category: 'lore' },
  { id: 'a24', name: 'Танцор балов',      desc: 'Используйте /танец с 50 разными игроками.',         points: 10, category: 'social' },
  { id: 'a25', name: 'Стрела рассвета',   desc: 'Завершите Рассветный Двор соло.',                   points: 35, category: 'dungeons' },
  { id: 'a26', name: 'Маршал войны',      desc: 'Достигните звания «Маршал».',                       points: 50, category: 'pvp' },
  { id: 'a27', name: 'Рунный мастер',     desc: 'Зачаруйте 100 предметов.',                          points: 20, category: 'professions' },
  { id: 'a28', name: 'Легендарное оружие',desc: 'Получите легендарный предмет.',                     points: 100, category: 'rewards' },
  { id: 'a29', name: 'Защитник Аиваре',   desc: 'Возглавьте рейд в час осады столицы.',              points: 40, category: 'lore' },
  { id: 'a30', name: 'Друг богов',        desc: 'Достигните «Превознесения» с 5 фракциями.',         points: 45, category: 'rewards' },
];

/* ─────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────── */

function rarityFor(points: number): { border: string; label: string } {
  if (points >= 50) return { border: '#FFD479', label: 'gold' };
  if (points >= 20) return { border: '#C9C9C9', label: 'silver' };
  return { border: '#C68A4A', label: 'bronze' };
}

function categoryColour(cat: Exclude<CategoryId, 'all'>): string {
  return CATEGORIES.find((c) => c.id === cat)?.colour ?? '#C9A96A';
}

function categoryNumber(cat: Exclude<CategoryId, 'all'>): number {
  return CATEGORIES.findIndex((c) => c.id === cat);
}

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

export function AchievementsPanel() {
  const close = () => useUiPanels.getState().set('achievements', false);
  const [active, setActive] = useState<CategoryId>('all');
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState('');

  /* ── Esc closes ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ACHIEVEMENTS.filter((a) => {
      if (active !== 'all' && a.category !== active) return false;
      if (q && !a.name.toLowerCase().includes(q) && !a.desc.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [active, query]);

  const totalEarned = ACHIEVEMENTS.filter((a) => a.earnedAt).length;
  const totalPoints = ACHIEVEMENTS.filter((a) => a.earnedAt).reduce((s, a) => s + a.points, 0);

  return (
    <div role="dialog" aria-modal style={overlayStyle} onClick={close}>
      <div onClick={(e) => e.stopPropagation()} style={shellStyle}>
        <SirusFrame size="md" style={{ width: 880, height: 580, position: 'relative' }}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Достижения</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={statPillStyle}>{totalEarned} / {ACHIEVEMENTS.length}</span>
              <span style={{ ...statPillStyle, color: '#FFD479' }}>{totalPoints} оч.</span>
              <button type="button" onClick={close} style={closeBtnStyle} aria-label="Закрыть">×</button>
            </div>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 0 10px' }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск достижений…"
              style={searchInputStyle}
            />
          </div>

          {/* Body */}
          <div style={bodyStyle}>
            {/* Category list */}
            <div style={{ ...categoryColStyle, width: collapsed ? 36 : 180 }}>
              <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                style={collapseBtnStyle}
                title={collapsed ? 'Развернуть' : 'Свернуть'}
              >
                {collapsed ? '»' : '«'}
              </button>
              {!collapsed && CATEGORIES.map((c) => {
                const isActive = c.id === active;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActive(c.id)}
                    style={{
                      ...categoryBtnStyle,
                      background: isActive ? 'rgba(201,169,106,0.15)' : 'transparent',
                      color: isActive ? '#FFD479' : 'var(--aiwari-fg-muted, #B19B7E)',
                      borderLeftColor: isActive ? c.colour : 'transparent',
                    }}
                  >
                    <span style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: c.colour, boxShadow: '0 0 4px rgba(0,0,0,0.6)',
                    }} />
                    {c.label}
                  </button>
                );
              })}
            </div>

            {/* Grid */}
            <div style={gridWrapStyle}>
              {filtered.length === 0 && (
                <div style={emptyStyle}>Ничего не найдено по запросу.</div>
              )}
              <div style={gridStyle}>
                {filtered.map((a) => {
                  const rar = rarityFor(a.points);
                  const colour = categoryColour(a.category);
                  const num = categoryNumber(a.category);
                  const earned = !!a.earnedAt;
                  return (
                    <div
                      key={a.id}
                      style={{
                        ...cardStyle,
                        borderColor: rar.border,
                        opacity: earned ? 1 : 0.65,
                      }}
                      title={earned ? `Получено: ${a.earnedAt}` : 'Не получено'}
                    >
                      <div style={{
                        ...iconStyle,
                        background: `radial-gradient(circle at 35% 30%, ${colour}, #1A140F 80%)`,
                        borderColor: rar.border,
                      }}>
                        <span style={iconNumStyle}>{num}</span>
                      </div>
                      <div style={cardBodyStyle}>
                        <div style={cardNameStyle}>{a.name}</div>
                        <div style={cardDescStyle}>{a.desc}</div>
                      </div>
                      <div style={pointsPillStyle}>{a.points}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </SirusFrame>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────── */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 40,
};

const shellStyle: CSSProperties = {
  background: 'var(--aiwari-bg-card-solid, #161210)',
  color: 'var(--aiwari-fg, #E8E1DA)',
  fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: 10,
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const titleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 20,
  letterSpacing: 1,
  color: 'var(--aiwari-gold-soft, #FFD479)',
};

const statPillStyle: CSSProperties = {
  padding: '3px 10px',
  background: 'rgba(11,9,7,0.7)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  borderRadius: 999,
  fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
  fontSize: 12,
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const closeBtnStyle: CSSProperties = {
  width: 28,
  height: 28,
  background: 'transparent',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontSize: 18,
  lineHeight: 1,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const searchInputStyle: CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  background: 'rgba(11,9,7,0.7)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg, #E8E1DA)',
  fontFamily: 'inherit',
  fontSize: 14,
  outline: 'none',
};

const bodyStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  gap: 12,
  minHeight: 0,
  height: 460,
};

const categoryColStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 0',
  borderRight: '1px solid var(--aiwari-frame-edge, #5C4520)',
  background: 'rgba(11,9,7,0.4)',
  transition: 'width 160ms',
  overflow: 'hidden',
};

const collapseBtnStyle: CSSProperties = {
  alignSelf: 'flex-end',
  marginRight: 4,
  marginBottom: 6,
  width: 24,
  height: 24,
  background: 'transparent',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontSize: 14,
  cursor: 'pointer',
};

const categoryBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  background: 'transparent',
  border: 'none',
  borderLeft: '3px solid transparent',
  textAlign: 'left',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  letterSpacing: 0.4,
  cursor: 'pointer',
};

const gridWrapStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  paddingRight: 6,
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 10,
};

const cardStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: 8,
  background: 'linear-gradient(180deg, #1F1813, #0B0907)',
  border: '2px solid #5C4520',
  borderRadius: 3,
  minHeight: 92,
};

const iconStyle: CSSProperties = {
  flexShrink: 0,
  width: 64,
  height: 64,
  borderRadius: '50%',
  border: '2px solid #5C4520',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'inset 0 0 6px rgba(0,0,0,0.7)',
};

const iconNumStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 22,
  color: '#FFD479',
  textShadow: '0 1px 2px rgba(0,0,0,0.8)',
};

const cardBodyStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
};

const cardNameStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  color: 'var(--aiwari-gold-soft, #FFD479)',
  letterSpacing: 0.3,
};

const cardDescStyle: CSSProperties = {
  fontStyle: 'italic',
  fontSize: 12,
  lineHeight: 1.35,
  color: 'var(--aiwari-fg-muted, #B19B7E)',
};

const pointsPillStyle: CSSProperties = {
  position: 'absolute',
  right: 6,
  bottom: 6,
  padding: '1px 8px',
  background: 'rgba(0,0,0,0.65)',
  border: '1px solid #C68A4A',
  borderRadius: 999,
  fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
  fontSize: 11,
  color: '#FFD479',
};

const emptyStyle: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  fontStyle: 'italic',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
};

export default AchievementsPanel;
